import React, {
  useState,
  forwardRef,
  useRef,
  useEffect,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { Box, Text, Icon, InputPrimitive, isWeb } from "@xsolla/xui-primitives";
import {
  useResolvedTheme,
  useId,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";
import { CheckCr, Remove, Location, Search } from "@xsolla/xui-icons-base";
import { allCountries as defaultCountries } from "./countries";

/**
 * Country data for phone input
 */
export interface Country {
  /** ISO 3166-1 alpha-2 country code (e.g., "US", "GB") */
  code: string;
  /** Country name */
  name: string;
  /** International dial code (e.g., "+1", "+44") */
  dialCode: string;
  /** Flag icon component */
  flag?: ReactNode;
}

export interface InputPhoneProps
  extends
    Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "onChange">,
    ThemeOverrideProps {
  /**
   * Property for specifying the value of the control.
   */
  value?: string;
  /**
   * Property for specifying the placeholder of the control.
   */
  placeholder?: string;
  /**
   * Event handler when the value changes (for controlled mode).
   */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Event handler when the text changes (alternative to onChange).
   */
  onChangeText?: (text: string) => void;
  /**
   * Property for changing the size of the input.
   */
  size?: "xl" | "lg" | "md" | "sm" | "xs";
  /**
   * Property for specifying the name of the control.
   */
  name?: string;
  /**
   * Property for disabling the control.
   */
  disabled?: boolean;
  /**
   * Property for displaying an error message and highlighting the control as invalid.
   */
  errorMessage?: string;
  /**
   * Property for displaying an error and highlighting the control as invalid.
   */
  error?: boolean;
  /**
   * Array of countries to display in the dropdown.
   * Defaults to all 200+ countries if not provided.
   */
  countries?: Country[];
  /**
   * Currently selected country.
   */
  selectedCountry?: Country;
  /**
   * Callback when a country is selected.
   */
  onCountryChange?: (country: Country) => void;
  /**
   * Add function clear for input.
   */
  extraClear?: boolean;
  /**
   * Function triggered when user clicks on extraClear button.
   */
  onRemove?: () => void;
  /**
   * Property for show checked status in input. Show only if not errorMessage.
   */
  checked?: boolean;
  /**
   * Property for passing a new icon to replace the default checked icon.
   */
  checkedIcon?: ReactNode;
  /**
   * Unique identifier for the input element. Used for accessibility linking.
   */
  id?: string;
  /**
   * Accessible label for screen readers when no visible label is present.
   */
  "aria-label"?: string;
  /**
   * Test identifier for the component.
   */
  testID?: string;
}

// Chevron down icon - inline SVG for now
const ChevronDown = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.5 6.75L9 11.25L13.5 6.75"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Chevron up icon for open dropdown
const ChevronUp = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.5 11.25L9 6.75L13.5 11.25"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const InputPhone = forwardRef<HTMLInputElement, InputPhoneProps>(
  (
    {
      value,
      placeholder = "+1 (000) 000-0000",
      onChange,
      onChangeText,
      onKeyDown,
      size = "md",
      name,
      disabled = false,
      errorMessage,
      error,
      countries = defaultCountries,
      selectedCountry: selectedCountryProp,
      onCountryChange,
      extraClear = false,
      onRemove,
      checked = false,
      checkedIcon = <CheckCr />,
      id: providedId,
      "aria-label": ariaLabel,
      testID,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const [internalState, setInternalState] = useState<"default" | "focus">(
      "default"
    );
    const [passValue, setPassValue] = useState(value ?? "");
    const [internalCountry, setInternalCountry] = useState<Country | undefined>(
      selectedCountryProp
    );
    const isCountryControlled = selectedCountryProp !== undefined;
    const selectedCountry = isCountryControlled
      ? selectedCountryProp
      : internalCountry;
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [searchQuery, setSearchQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const countryButtonRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const selectedCountryRef = useRef<HTMLDivElement>(null);
    const countryListRef = useRef<HTMLDivElement>(null);

    // Generate unique IDs for accessibility
    const rawId = useId();
    const safeId = rawId.replace(/:/g, "");
    const inputId = providedId || `input-phone-${safeId}`;
    const errorId = `${inputId}-error`;
    const dropdownId = `${inputId}-dropdown`;

    // Forward ref to input element
    React.useImperativeHandle(
      ref,
      () => inputRef.current as HTMLInputElement,
      []
    );

    // Sync passValue with value prop when it changes
    useEffect(() => {
      if (value !== undefined) {
        setPassValue(value);
      }
    }, [value]);

    // Auto-scroll to selected country when dropdown opens (show at top)
    useEffect(() => {
      if (
        isDropdownOpen &&
        selectedCountryRef.current &&
        countryListRef.current
      ) {
        // Use setTimeout to ensure DOM has rendered after search input focus
        const timeoutId = setTimeout(() => {
          const selectedItem = selectedCountryRef.current;
          if (selectedItem) {
            selectedItem.scrollIntoView({ block: "nearest" });
          }
        }, 50);
        return () => clearTimeout(timeoutId);
      }
    }, [isDropdownOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isDropdownOpen || !isWeb) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(target) &&
          countryButtonRef.current &&
          !countryButtonRef.current.contains(target)
        ) {
          setIsDropdownOpen(false);
          setHighlightedIndex(-1);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isDropdownOpen]);

    const isDisable = disabled;
    const isError = !!(errorMessage || error);
    // Separate focus states for selector and input
    const isSelectorFocus = isDropdownOpen;
    const isInputFocus = internalState === "focus";

    // Handle checked status (only show if not error)
    const isCheckedShown = checked && !errorMessage;
    // Clear button visibility
    const isExtraClearIconShown = !disabled && extraClear && !!passValue;
    const extrasCount = Number(isExtraClearIconShown) + Number(isCheckedShown);

    // Resolve Config from Theme
    const sizeStyles = theme.sizing.input(size);
    const inputColors = theme.colors.control.input;

    const handleFocus = () => {
      if (!isDisable) {
        setInternalState("focus");
      }
    };

    const handleBlur = () => {
      if (!isDisable) {
        setInternalState("default");
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (onChange) {
        onChange(e);
      }
      if (onChangeText) {
        onChangeText(newValue);
      }

      setPassValue(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        if (isDropdownOpen) {
          setIsDropdownOpen(false);
          setHighlightedIndex(-1);
        } else {
          e.currentTarget.blur();
        }
      }
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    const handleClear = () => {
      setPassValue("");
      onRemove?.();

      if (inputRef.current) {
        if (typeof window !== "undefined" && window.HTMLInputElement) {
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype,
            "value"
          )?.set;

          if (nativeInputValueSetter) {
            nativeInputValueSetter.call(inputRef.current, "");
          }
        }

        const syntheticEvent = {
          target: inputRef.current,
          currentTarget: inputRef.current,
          type: "change",
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);
        inputRef.current.focus();
      }
    };

    const toggleDropdown = () => {
      if (!isDisable) {
        const opening = !isDropdownOpen;
        setIsDropdownOpen(opening);
        setHighlightedIndex(-1);
        if (opening) {
          setSearchQuery("");
          // Focus search input when opening
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }
      }
    };

    const handleCountrySelect = (country: Country) => {
      if (!isCountryControlled) {
        setInternalCountry(country);
      }
      onCountryChange?.(country);
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      setSearchQuery("");
      inputRef.current?.focus();
    };

    // Filter countries based on search query
    const filteredCountries = searchQuery
      ? countries.filter(
          (country) =>
            country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            country.dialCode.includes(searchQuery) ||
            country.code.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : countries;

    const handleCountryKeyDown = (e: React.KeyboardEvent) => {
      if (isDisable) return;

      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          toggleDropdown();
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!isDropdownOpen) {
            setIsDropdownOpen(true);
            setHighlightedIndex(0);
          } else {
            setHighlightedIndex((prev) =>
              prev < filteredCountries.length - 1 ? prev + 1 : prev
            );
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (isDropdownOpen) {
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          }
          break;
        case "Escape":
          if (isDropdownOpen) {
            e.preventDefault();
            setIsDropdownOpen(false);
            setHighlightedIndex(-1);
          }
          break;
      }
    };

    // Select highlighted country on Enter in dropdown
    useEffect(() => {
      if (
        isDropdownOpen &&
        highlightedIndex >= 0 &&
        highlightedIndex < filteredCountries.length
      ) {
        const handleEnter = (e: KeyboardEvent) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleCountrySelect(filteredCountries[highlightedIndex]);
          }
        };
        document.addEventListener("keydown", handleEnter);
        return () => document.removeEventListener("keydown", handleEnter);
      }
    }, [isDropdownOpen, highlightedIndex, filteredCountries]);

    // Resolve background and border colors based on state - separate for selector and input
    let selectorBgColor = inputColors.bg;
    let selectorBorderColor = inputColors.border;
    let selectorOutlineColor: string | undefined;

    let inputBgColor = inputColors.bg;
    let inputBorderColor = inputColors.border;
    let inputOutlineColor: string | undefined;

    if (isDisable) {
      selectorBgColor = inputColors.bgDisable;
      selectorBorderColor = inputColors.borderDisable;
      inputBgColor = inputColors.bgDisable;
      inputBorderColor = inputColors.borderDisable;
    } else if (isError) {
      // Error state: only input shows red outline, selector stays default
      inputOutlineColor = theme.colors.border.alert;
      if (isSelectorFocus) {
        selectorBgColor = theme.colors.control.focus.bg;
        selectorOutlineColor = theme.colors.border.brand;
      }
      if (isInputFocus) {
        inputBgColor = theme.colors.control.focus.bg;
      }
    } else {
      // Selector focus state (dropdown open)
      if (isSelectorFocus) {
        selectorBgColor = theme.colors.control.focus.bg;
        selectorOutlineColor = theme.colors.border.brand;
      }
      // Input focus state
      if (isInputFocus) {
        inputBgColor = theme.colors.control.focus.bg;
        inputOutlineColor = theme.colors.border.brand;
      }
    }

    const textColor = isDisable ? inputColors.textDisable : inputColors.text;
    const placeholderColor = inputColors.placeholder;
    const iconColor = isDisable ? inputColors.placeholder : inputColors.text;

    // Padding values from Figma design
    const paddingConfig: Record<
      string,
      { vertical: number; horizontal: number }
    > = {
      xl: { vertical: 12, horizontal: 12 },
      lg: { vertical: 14, horizontal: 12 },
      md: { vertical: 11, horizontal: 12 },
      sm: { vertical: 7, horizontal: 10 },
      xs: { vertical: 7, horizontal: 10 },
    };

    // Icon sizes from Figma design
    const iconSizeConfig: Record<string, number> = {
      xl: 18,
      lg: 18,
      md: 18,
      sm: 16,
      xs: 16,
    };

    // Flag sizes from Figma design (16x12 aspect ratio)
    const flagSizeConfig: Record<string, number> = {
      xl: 18,
      lg: 18,
      md: 18,
      sm: 16,
      xs: 14,
    };

    // Country selector width by size
    const selectorWidthConfig: Record<string, number> = {
      xl: 78,
      lg: 72,
      md: 66,
      sm: 60,
      xs: 56,
    };

    // Focus outline config from Figma design
    const focusOutlineConfig: Record<
      string,
      { width: number; offset: number }
    > = {
      xl: { width: 1, offset: -1 },
      lg: { width: 1, offset: -1 },
      md: { width: 1, offset: -1 },
      sm: { width: 1, offset: -1 },
      xs: { width: 1, offset: -1 },
    };

    const padding = paddingConfig[size];
    const borderRadius = theme.shape.input[size].borderRadius;
    const iconSize = iconSizeConfig[size];
    const flagSize = flagSizeConfig[size];
    const selectorWidth = selectorWidthConfig[size];
    const focusOutline = focusOutlineConfig[size];

    // Dropdown styles (web only)
    const dropdownStyles = isWeb
      ? {
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          maxHeight: 240,
          overflowY: "auto" as const,
        }
      : {};

    return (
      <Box
        flexDirection="column"
        gap={sizeStyles.fieldGap}
        width="100%"
        testID={testID}
      >
        <Box
          flexDirection="row"
          alignItems="stretch"
          height={sizeStyles.height}
          position="relative"
          gap={2}
        >
          {/* Country Selector */}
          <Box
            as="button"
            type="button"
            ref={countryButtonRef}
            onPress={toggleDropdown}
            onKeyDown={handleCountryKeyDown}
            backgroundColor={selectorBgColor}
            borderColor={selectorBorderColor}
            borderWidth={1}
            style={{
              borderTopLeftRadius: borderRadius,
              borderBottomLeftRadius: borderRadius,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              opacity: 1,
              ...(selectorOutlineColor
                ? {
                    outline: `${focusOutline.width}px solid ${selectorOutlineColor}`,
                    outlineOffset: `${focusOutline.offset}px`,
                  }
                : {}),
            }}
            width={selectorWidth}
            paddingHorizontal={padding.horizontal}
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            gap={4}
            cursor={isDisable ? "not-allowed" : "pointer"}
            disabled={isDisable}
            role="combobox"
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
            aria-controls={dropdownId}
            aria-label={
              selectedCountry
                ? `Selected country: ${selectedCountry.name}`
                : "Select country"
            }
            hoverStyle={
              !isDisable && !isSelectorFocus && !isError
                ? {
                    backgroundColor: inputColors.bgHover,
                    borderColor: inputColors.borderHover,
                  }
                : undefined
            }
          >
            {selectedCountry?.flag ? (
              <Box width={flagSize} height={flagSize}>
                {React.isValidElement(selectedCountry.flag)
                  ? React.cloneElement(
                      selectedCountry.flag as React.ReactElement<{
                        size?: number;
                      }>,
                      { size: flagSize }
                    )
                  : selectedCountry.flag}
              </Box>
            ) : (
              <Location size={flagSize} variant="line" color={iconColor} />
            )}
            <Icon size={iconSize} color={iconColor}>
              {isDropdownOpen ? <ChevronUp /> : <ChevronDown />}
            </Icon>
          </Box>

          {/* Phone Number Input */}
          <Box
            flex={1}
            backgroundColor={inputBgColor}
            borderColor={inputBorderColor}
            borderWidth={1}
            paddingVertical={padding.vertical}
            paddingHorizontal={padding.horizontal}
            flexDirection="row"
            alignItems="center"
            gap={10}
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: borderRadius,
              borderBottomRightRadius: borderRadius,
              opacity: 1,
              ...(inputOutlineColor
                ? {
                    outline: `${focusOutline.width}px solid ${inputOutlineColor}`,
                    outlineOffset: `${focusOutline.offset}px`,
                  }
                : {}),
            }}
            hoverStyle={
              !isDisable && !isInputFocus && !isError
                ? {
                    backgroundColor: inputColors.bgHover,
                    borderColor: inputColors.borderHover,
                  }
                : undefined
            }
          >
            <Box flex={1} height="100%" justifyContent="center">
              <InputPrimitive
                ref={inputRef}
                id={inputId}
                value={passValue}
                name={name}
                placeholder={placeholder}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={isDisable}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                color={textColor}
                fontSize={sizeStyles.fontSize}
                placeholderTextColor={placeholderColor}
                aria-invalid={isError || undefined}
                aria-describedby={errorMessage ? errorId : undefined}
                aria-label={ariaLabel || "Phone number"}
                aria-disabled={isDisable || undefined}
                data-testid="input-phone__field"
              />
            </Box>

            {/* Right-side Extras Wrapper */}
            {extrasCount > 0 && (
              <Box flexDirection="row" alignItems="center" gap={4}>
                {isExtraClearIconShown && (
                  <Box
                    as="button"
                    type="button"
                    alignItems="center"
                    justifyContent="center"
                    width={iconSize}
                    height="100%"
                    backgroundColor="transparent"
                    borderWidth={0}
                    cursor={disabled ? "not-allowed" : "pointer"}
                    onPress={!disabled ? handleClear : undefined}
                    disabled={disabled}
                    aria-label="Clear phone number"
                    data-testid="input-phone__clear-button"
                  >
                    <Icon size={iconSize} color={textColor}>
                      <Remove />
                    </Icon>
                  </Box>
                )}
                {isCheckedShown && (
                  <Box
                    alignItems="center"
                    justifyContent="center"
                    width={iconSize}
                    height="100%"
                    aria-label="Valid phone number"
                  >
                    <Icon
                      size={iconSize}
                      color={theme.colors.content.success.primary}
                    >
                      {checkedIcon}
                    </Icon>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Country Dropdown Menu */}
          {isDropdownOpen && countries.length > 0 && (
            <Box
              ref={dropdownRef}
              position="absolute"
              top={sizeStyles.height + 4}
              left={0}
              right={0}
              backgroundColor={theme.colors.layer.float}
              borderColor={theme.colors.border.secondary}
              borderWidth={1}
              borderRadius={borderRadius}
              zIndex={1000}
              role="listbox"
              id={dropdownId}
              aria-label="Country list"
              style={dropdownStyles}
            >
              {/* Search Input - using contextMenu sizing from Figma */}
              {(() => {
                // Map input sizes to contextMenu sizes (xs maps to sm)
                const contextMenuSize = size === "xs" ? "sm" : size;
                const searchSizing = theme.sizing.contextMenu(contextMenuSize);
                return (
                  <Box
                    flexDirection="column"
                    gap={searchSizing.searchGap}
                    paddingHorizontal={searchSizing.searchPaddingHorizontal}
                    paddingVertical={searchSizing.searchPaddingVertical}
                  >
                    <Box flexDirection="row" alignItems="center" gap={8}>
                      <Search
                        size={searchSizing.searchIconSize}
                        variant="line"
                        color={inputColors.placeholder}
                      />
                      <Box flex={1}>
                        <InputPrimitive
                          ref={searchInputRef}
                          value={searchQuery}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setSearchQuery(e.target.value);
                            setHighlightedIndex(0);
                          }}
                          placeholder="Search"
                          fontSize={searchSizing.searchFontSize}
                          color={textColor}
                          placeholderTextColor={placeholderColor}
                          aria-label="Search countries"
                          aria-controls={`${dropdownId}-list`}
                          aria-activedescendant={
                            highlightedIndex >= 0 &&
                            filteredCountries[highlightedIndex]
                              ? `${dropdownId}-option-${filteredCountries[highlightedIndex].code}`
                              : undefined
                          }
                          data-testid="input-phone__search"
                          style={{
                            lineHeight: `${searchSizing.searchLineHeight}px`,
                          }}
                          onKeyDown={(
                            e: React.KeyboardEvent<HTMLInputElement>
                          ) => {
                            if (e.key === "Escape") {
                              setIsDropdownOpen(false);
                              setSearchQuery("");
                              countryButtonRef.current?.focus();
                            } else if (e.key === "ArrowDown") {
                              e.preventDefault();
                              setHighlightedIndex((prev) =>
                                prev < filteredCountries.length - 1
                                  ? prev + 1
                                  : prev
                              );
                            } else if (e.key === "ArrowUp") {
                              e.preventDefault();
                              setHighlightedIndex((prev) =>
                                prev > 0 ? prev - 1 : prev
                              );
                            } else if (
                              e.key === "Enter" &&
                              filteredCountries.length > 0
                            ) {
                              e.preventDefault();
                              const idx =
                                highlightedIndex >= 0 ? highlightedIndex : 0;
                              handleCountrySelect(filteredCountries[idx]);
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      height={1}
                      backgroundColor={theme.colors.border.secondary}
                      alignSelf="stretch"
                    />
                  </Box>
                );
              })()}

              {/* Country List */}
              <Box
                ref={countryListRef}
                id={`${dropdownId}-list`}
                paddingVertical={4}
                style={{ maxHeight: 200, overflowY: "auto" as const }}
              >
                {filteredCountries.length === 0 ? (
                  <Box
                    paddingHorizontal={padding.horizontal}
                    paddingVertical={12}
                  >
                    <Text
                      color={theme.colors.content.secondary}
                      fontSize={sizeStyles.fontSize}
                      textAlign="center"
                    >
                      No countries found
                    </Text>
                  </Box>
                ) : (
                  filteredCountries.map((country, index) => {
                    const isSelected = selectedCountry?.code === country.code;
                    const brandColors = theme.colors.control.brand.primary;
                    const contentColors = theme.colors.content;

                    // Background color: selected = cyan, highlighted = hover, else transparent
                    const getBgColor = () => {
                      if (isSelected) return brandColors.bg;
                      if (highlightedIndex === index)
                        return inputColors.bgHover;
                      return "transparent";
                    };

                    // Text color: selected = black (on.brand), else primary/secondary
                    const getTextColor = () => {
                      if (isSelected) return contentColors.on.brand;
                      return theme.colors.content.primary;
                    };

                    const getSecondaryTextColor = () => {
                      if (isSelected) return contentColors.on.brand;
                      return theme.colors.content.secondary;
                    };

                    return (
                      <Box
                        key={country.code}
                        ref={isSelected ? selectedCountryRef : undefined}
                        id={`${dropdownId}-option-${country.code}`}
                        as="button"
                        type="button"
                        paddingHorizontal={padding.horizontal}
                        paddingVertical={8}
                        onPress={() => handleCountrySelect(country)}
                        flexDirection="row"
                        alignItems="center"
                        gap={8}
                        width="100%"
                        backgroundColor={getBgColor()}
                        borderWidth={0}
                        cursor="pointer"
                        role="option"
                        aria-selected={isSelected}
                        hoverStyle={
                          !isSelected
                            ? {
                                backgroundColor: inputColors.bgHover,
                              }
                            : undefined
                        }
                      >
                        {country.flag ? (
                          <Box width={flagSize} height={flagSize}>
                            {React.isValidElement(country.flag)
                              ? React.cloneElement(
                                  country.flag as React.ReactElement<{
                                    size?: number;
                                  }>,
                                  { size: flagSize }
                                )
                              : country.flag}
                          </Box>
                        ) : (
                          <Box
                            width={flagSize}
                            height={Math.round(flagSize * 0.75)}
                            backgroundColor={theme.colors.background.secondary}
                            borderRadius={2}
                          />
                        )}
                        <Text
                          color={getTextColor()}
                          fontSize={sizeStyles.fontSize}
                          style={{
                            flex: 1,
                            textAlign: "left",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {country.name}
                        </Text>
                        <Text
                          color={getSecondaryTextColor()}
                          fontSize={sizeStyles.fontSize}
                          style={{ textAlign: "right" }}
                        >
                          {country.dialCode}
                        </Text>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          )}
        </Box>

        {errorMessage && (
          <Text
            id={errorId}
            role="alert"
            color={theme.colors.content.alert.primary}
            fontSize={sizeStyles.fontSize - 2}
          >
            {errorMessage}
          </Text>
        )}
      </Box>
    );
  }
);

InputPhone.displayName = "InputPhone";
